import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
} from '@mui/material';
import { dateToSqlDate } from '../../helpers/dateFuncs';
import fetchData from '../../helpers/fetchData';
import { formatNumber } from '../../helpers/numberUtils';
import { exportToExcel } from '../../helpers/exportFuncs';
import { FilterContext, IFilterState } from '../../contexts/FilterContext';
import { getComparator, Order, sortedRowInformation } from '../../helpers/sortingsFuncs';
import LoadingSpinner from '../LoadingSpinner';
import { visuallyHidden } from '@mui/utils';

interface HeaderMappingItem {
  name: string;
  type: string;
  digits: number;
  attach: string;
}

const detailsHeaderMapping: Record<string, HeaderMappingItem> = {
  test_group: {
    name: 'Test Gruppe',
    type: 'string',
    digits: 0,
    attach: '',
  },
  leads: {
    name: 'Leads',
    type: 'number',
    digits: 0,
    attach: '',
  },
  total_cost: {
    name: 'Kosten',
    type: 'number',
    digits: 2,
    attach: '€',
  },
  revenue_30d: {
    name: 'Revenue (30d)',
    type: 'number',
    digits: 2,
    attach: '€',
  },
  revenue_total: {
    name: 'Revenue (Total)',
    type: 'number',
    digits: 2,
    attach: '€',
  },
  anfos_30d: {
    name: 'Anfos 30d',
    type: 'number',
    digits: 0,
    attach: '',
  },
  avg_order_value: {
    name: '⌀ Abrechnungswert je Anfo (30 T.)',
    type: 'number',
    digits: 2,
    attach: '€',
  },
  conversion_rate: {
    name: 'Conversion Rate',
    type: 'number',
    digits: 2,
    attach: '%',
  },
  roi30: {
    name: 'ROI (30d)',
    type: 'number',
    digits: 2,
    attach: '%',
  },
  roi_total: {
    name: 'ROI (laufend)',
    type: 'number',
    digits: 2,
    attach: '%',
  },
};

type RoiTestData = {
  test_group: string;
  leads: number | null;
  total_cost: number | null;
  revenue_30d: number | null;
  revenue_total: number | null;
  anfos_30d: number | null;
  avg_order_value: number | null;
  conversion_rate: number | null;
  roi30: number | null;
  roi_total: number | null;
  [key: string]: any;
};

type Data = Record<DataKey, string>;
type DataKey = keyof RoiTestData;

const getData: (
  filterState: IFilterState,
  aggregation: 'detailed' | 'brand',
) => Promise<RoiTestData[]> = async (
  filterState: IFilterState,
  aggregation: 'detailed' | 'brand',
) => {
  if (!filterState.startDate || !filterState.endDate) {
    throw new Error('No start date or end date provided');
  }
  const networks = Array.isArray(filterState.network) ? filterState.network : [filterState.network];
  const themes = Array.isArray(filterState.theme)
    ? filterState.theme
    : filterState.theme
      ? [filterState.theme]
      : [];
  const options = {
    start_date: dateToSqlDate(filterState.startDate),
    end_date: dateToSqlDate(filterState.endDate),
    aggregation: aggregation,
    ...(networks.length > 0 && { network: networks }),
    ...(themes.length > 0 && { theme: themes }),
  };

  const roiData = (await fetchData<Record<string, any>[]>('/api/vnr/roi-tests', options)) as Record<
    string,
    any
  >[];

  return roiData.map((item) => ({
    test_group: item.test_group,
    leads: parseInt(item.leads) || 0,
    total_cost: parseFloat(item.total_cost) || 0,
    revenue_30d: parseFloat(item.revenue_30d) || 0,
    revenue_total: parseFloat(item.revenue_total) || 0,
    anfos_30d: parseInt(item.anfos_30d) || 0,
    avg_order_value: item.avg_order_value ? parseFloat(item.avg_order_value) : null,
    conversion_rate: item.conversion_rate ? parseFloat(item.conversion_rate) : null,
    roi30: item.roi30 ? parseFloat(item.roi30) : null,
    roi_total: item.roi_total ? parseFloat(item.roi_total) : null,
  }));
};

const computeSummary = (data: RoiTestData[]): RoiTestData => {
  if (!data.length) {
    return {
      test_group: 'Insgesamt',
      leads: 0,
      total_cost: 0,
      revenue_30d: 0,
      revenue_total: 0,
      anfos_30d: 0,
      avg_order_value: null,
      conversion_rate: null,
      roi30: 0,
      roi_total: 0,
    };
  }

  const totalLeads = data.reduce((acc, curr) => acc + (parseInt(String(curr.leads)) || 0), 0);
  const totalCost = data.reduce((acc, curr) => acc + (parseFloat(String(curr.total_cost)) || 0), 0);
  const totalRevenue30d = data.reduce(
    (acc, curr) => acc + (parseFloat(String(curr.revenue_30d)) || 0),
    0,
  );
  const totalRevenueTotal = data.reduce(
    (acc, curr) => acc + (parseFloat(String(curr.revenue_total)) || 0),
    0,
  );
  const totalAnfos30d = data.reduce(
    (acc, curr) => acc + (parseInt(String(curr.anfos_30d)) || 0),
    0,
  );

  const roi30 = totalCost > 0 ? (totalRevenue30d / totalCost) * 100 : 0;
  const roiTotal = totalCost > 0 ? (totalRevenueTotal / totalCost) * 100 : 0;
  const avgOrderValue = totalAnfos30d > 0 ? totalRevenue30d / totalAnfos30d : null;
  const conversionRate = totalLeads > 0 ? (totalAnfos30d / totalLeads) * 100 : null;

  return {
    test_group: 'Insgesamt',
    leads: totalLeads,
    total_cost: totalCost,
    revenue_30d: totalRevenue30d,
    revenue_total: totalRevenueTotal,
    anfos_30d: totalAnfos30d,
    avg_order_value: avgOrderValue,
    conversion_rate: conversionRate,
    roi30: roi30,
    roi_total: roiTotal,
  };
};

const getTestGroupColor = (testGroup: string | null): string => {
  if (!testGroup) return '#9e9e9e';
  if (testGroup.includes('CR-')) return '#9c27b0'; // purple - crypto
  if (testGroup.includes('LC-')) return '#00bcd4'; // cyan - large cap
  if (testGroup.includes('SC-')) return '#e91e63'; // pink - small cap
  if (testGroup.includes('TS-')) return '#795548'; // brown - trend sector
  if (testGroup === 'INVESTOR' || testGroup.includes('INV')) return '#4caf50'; // green
  if (testGroup === 'GEVESTOR' || testGroup.includes('GEV')) return '#2196f3'; // blue
  if (testGroup === 'ALREADY_CUSTOMER') return '#ff9800';
  return '#9e9e9e';
};

const getTestGroupLabel = (testGroup: string | null): string => {
  if (!testGroup) return 'Unknown';
  if (testGroup === 'INVESTOR') return 'INVESTOR';
  if (testGroup === 'GEVESTOR') return 'GEVESTOR';
  if (testGroup === 'ALREADY_CUSTOMER') return 'Already Customer';

  // Detailed mode: "CR-50 (INV)" → "Crypto 50% (Investor)"
  const match = testGroup.match(/^(INV|GEV|CR|LC|SC|TS)-(\d+)\s*\((\w+)\)$/);
  if (match) {
    const [, type, pct, customer] = match;
    const typeLabels: Record<string, string> = {
      INV: 'Investor',
      GEV: 'GeVestor',
      CR: 'Crypto',
      LC: 'Large Cap',
      SC: 'Small Cap',
      TS: 'Trend Sector',
    };
    const custLabel = customer === 'INV' ? 'Investor' : 'GeVestor';
    return `${typeLabels[type]} ${pct}% (${custLabel})`;
  }

  // Handle AC (GEV) or AC (INV) format
  const acMatch = testGroup.match(/^AC\s*\((\w+)\)$/);
  if (acMatch) {
    const custLabel = acMatch[1] === 'INV' ? 'Investor' : 'GeVestor';
    return `Already Customer (${custLabel})`;
  }

  return testGroup;
};

interface RoiTestsTableProps {
  aggregation: 'detailed' | 'brand';
  // When set, only rows whose test_group is in this exact-match list are shown
  // (the cohorts of the selected test + regime, e.g. ['TS-70 (GEV)', 'TS-30 (INV)']).
  testGroupFilter?: string[];
}

export const RoiTestsTable: React.FC<RoiTestsTableProps> = ({ aggregation, testGroupFilter }) => {
  const { filterState } = useContext(FilterContext)!;
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<DataKey>('leads');
  const [data, setData] = useState<RoiTestData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!filterState.startDate || !filterState.endDate) {
      return;
    }

    setLoading(true);
    const fetchDataAsync = async () => {
      try {
        const data = await getData(filterState, aggregation);
        setData(data);
        setLoading(false);
      } catch (err: any) {
        setError(err);
        setLoading(false);
      }
    };

    fetchDataAsync();
  }, [
    filterState.startDate,
    filterState.endDate,
    filterState.network,
    filterState.theme,
    aggregation,
  ]);

  if (!filterState.startDate || !filterState.endDate) {
    throw new Error('No start date or end date provided');
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  const headers = Object.keys(detailsHeaderMapping);

  const handleRequestSort = (property: keyof Data) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const createSortHandler = (property: keyof Data) => () => {
    handleRequestSort(property);
  };

  const filteredData = testGroupFilter
    ? data.filter((row) => testGroupFilter.includes(row.test_group))
    : data;
  const sortedData = sortedRowInformation<RoiTestData>(filteredData, getComparator(order, orderBy));

  return (
    <Box bgcolor="#fff">
      <Box>
        <Button
          onClick={() =>
            exportToExcel([...sortedData, computeSummary(filteredData)], 'ROI-Tests-AB-Vergleich')
          }
          sx={{ float: 'right' }}
        >
          Download als Excel
        </Button>
      </Box>
      <Box>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                {headers.map((header) => (
                  <TableCell key={header}>
                    <TableSortLabel
                      active={orderBy === header}
                      direction={orderBy === header ? order : 'asc'}
                      onClick={createSortHandler(header)}
                    >
                      {detailsHeaderMapping[header].name || header}
                      {orderBy === header ? (
                        <Box component="span" sx={{ ...visuallyHidden }}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    '&:nth-of-type(odd)': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  {Object.entries(detailsHeaderMapping).map(([key, value]) => (
                    <TableCell key={key}>
                      {key === 'test_group' ? (
                        <Chip
                          label={getTestGroupLabel(row[key] as string)}
                          sx={{
                            backgroundColor: getTestGroupColor(row[key] as string),
                            color: '#fff',
                            fontWeight: 'bold',
                          }}
                        />
                      ) : value.type === 'number' ? (
                        row[key] === null ? (
                          ''
                        ) : (
                          formatNumber(Number(row[key]), value.digits) + (value.attach || '')
                        )
                      ) : (
                        row[key]
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {(() => {
                const summary = computeSummary(sortedData);
                return (
                  <TableRow
                    sx={{
                      backgroundColor: 'rgba(0, 0, 0, 0.08)',
                      fontWeight: 'bold',
                    }}
                  >
                    {Object.entries(detailsHeaderMapping).map(([key, value]) => (
                      <TableCell key={key} sx={{ fontWeight: 'bold' }}>
                        {value.type === 'number'
                          ? summary[key] === null
                            ? ''
                            : formatNumber(Number(summary[key]), value.digits) +
                              (value.attach || '')
                          : summary[key]}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })()}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default RoiTestsTable;
